import type { LucideIcon } from "lucide-react";
import {
    BarChart3,
    FolderOpen,
    GraduationCap,
    MessagesSquare,
    Stethoscope,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { DataKeys } from "../../types";
import { useLang } from "../Context";
import { getEffectiveLocationHash } from "../navigation/navPathUtils";
import { HealthClinicalAgentTabSelector } from "../health/aimmaDoctor/HealthClinicalAgentTabSelector";
import { HealthSection } from "../health/HealthSection";
import { HealthSectionHeader } from "../health/HealthSectionHeader";
import {
    getLocalized,
    HealthLang,
    LocalizedString,
} from "../health/healthTypes";
import { healthAimmaDoctorSectionContentOffset } from "../health/healthLayout";
import {
    healthClinicalAgentsActiveWrap,
    healthClinicalAgentsShell,
    healthProductSuiteIconGlyphFor,
} from "../health/healthVisualStyles";
import { EducationModuleDetailPanel } from "./EducationModuleDetailPanel";

const MODULE_PREVIEW_ASSETS: Record<string, string> = {
    "case-prep": "medical-sensei-case-prep",
    simulation: "medical-sensei-simulation",
    instructor: "medical-sensei-instructor",
    debate: "medical-sensei-debate",
    reflection: "medical-sensei-reflection",
};

const MODULE_ICONS: Record<string, LucideIcon> = {
    "case-prep": FolderOpen,
    simulation: Stethoscope,
    instructor: GraduationCap,
    debate: MessagesSquare,
    reflection: BarChart3,
};

const MODULE_ICON_STROKE = 1.75;

interface EducationModulesSectionProps {
    data: DataKeys;
}

function EducationModuleTabIcon({
    moduleId,
    isActive,
}: {
    moduleId: string;
    isActive: boolean;
}) {
    const Icon = MODULE_ICONS[moduleId] ?? FolderOpen;

    return (
        <Icon
            className={`shrink-0 !h-7 !w-7 lg:!h-9 lg:!w-9 ${
                isActive
                    ? healthProductSuiteIconGlyphFor("teal")
                    : "text-grey-400"
            }`}
            strokeWidth={MODULE_ICON_STROKE}
            aria-hidden="true"
        />
    );
}

export function EducationModulesSection({ data }: EducationModulesSectionProps) {
    const { lang } = useLang();
    const location = useLocation();
    const hLang = lang as HealthLang;
    const items = (data.items as DataKeys[]) ?? [];
    const metadataLabels = data.metadataLabels as DataKeys | undefined;
    const flowSummaryLabel = getLocalized(
        data.flowSummaryLabel as LocalizedString,
        hLang
    );
    const [activeIndex, setActiveIndex] = useState(0);
    const prevIndexRef = useRef(0);
    const panelIdPrefix = "modules";

    const selectModule = (index: number) => {
        prevIndexRef.current = activeIndex;
        setActiveIndex(index);
    };

    useEffect(() => {
        const hash = getEffectiveLocationHash(location.hash);

        if (!hash || hash === "#modules") {
            setActiveIndex((current) => {
                if (current === 0) return current;
                prevIndexRef.current = current;
                return 0;
            });
            return;
        }

        if (!hash.startsWith("#modules-")) return;

        const moduleId = hash.replace("#modules-", "");
        const index = items.findIndex((item) => item.id === moduleId);
        if (index >= 0) {
            setActiveIndex((current) => {
                prevIndexRef.current = current;
                return index;
            });
        }
    }, [items, location.hash, lang]);

    const activeItem = items[activeIndex];
    const activeModuleId = activeItem?.id as string | undefined;
    const transitionDirection =
        activeIndex >= prevIndexRef.current ? 1 : -1;
    const previewAssetKey = activeModuleId
        ? MODULE_PREVIEW_ASSETS[activeModuleId]
        : undefined;

    const tabListAriaLabel = getLocalized(
        data.tabListAriaLabel as LocalizedString,
        hLang
    );
    const tabPreviousAriaLabel = getLocalized(
        data.tabPreviousAriaLabel as LocalizedString,
        hLang
    );
    const tabNextAriaLabel = getLocalized(
        data.tabNextAriaLabel as LocalizedString,
        hLang
    );

    return (
        <HealthSection
            id="modules"
            ariaLabelledBy="modules-title"
            childPage
            className="scroll-mt-24"
        >
            <HealthSectionHeader
                eyebrow={getLocalized(data.eyebrow as LocalizedString, hLang)}
                title={getLocalized(data.title as LocalizedString, hLang)}
                titleId="modules-title"
                subtitle={getLocalized(data.intro as LocalizedString, hLang)}
                subtitleWide
            />

            <div
                className={`${healthAimmaDoctorSectionContentOffset} ${healthClinicalAgentsShell}`}
            >
                <HealthClinicalAgentTabSelector
                    blocks={items}
                    activeIndex={activeIndex}
                    onSelect={selectModule}
                    lang={hLang}
                    panelIdPrefix={panelIdPrefix}
                    tabListAriaLabel={tabListAriaLabel}
                    tabPreviousAriaLabel={tabPreviousAriaLabel}
                    tabNextAriaLabel={tabNextAriaLabel}
                    renderTabIcon={(block, isActive) => (
                        <EducationModuleTabIcon
                            moduleId={block.id as string}
                            isActive={isActive}
                        />
                    )}
                />

                <div className={healthClinicalAgentsActiveWrap}>
                    {activeItem ? (
                        <EducationModuleDetailPanel
                            item={activeItem}
                            metadataLabels={metadataLabels}
                            flowSummaryLabel={flowSummaryLabel}
                            lang={hLang}
                            transitionDirection={transitionDirection}
                            panelId={`${panelIdPrefix}-${activeModuleId}`}
                            tabId={`${panelIdPrefix}-${activeModuleId}-tab`}
                            previewAssetKey={previewAssetKey}
                        />
                    ) : null}
                </div>
            </div>
        </HealthSection>
    );
}
